import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Edit, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MenuManagement = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [newCategory, setNewCategory] = useState("");
  const [itemForm, setItemForm] = useState({
    name: "",
    description: "",
    price: "",
    image_url: "",
    category: "",
    available: true
  });
  const [editingItem, setEditingItem] = useState(null);
  const [showItemDialog, setShowItemDialog] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin-vow");
      return;
    }
    loadData();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      loadMenuForBrand(selectedBrand);
    }
  }, [selectedBrand]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
      if (response.data.length > 0) {
        setSelectedBrand(response.data[0].name);
      }
    } catch (error) {
      console.error("Error loading brands:", error);
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  };

  const loadMenuForBrand = async (brandName) => {
    try {
      const token = localStorage.getItem("admin_token");
      const [categoriesRes, menuRes] = await Promise.all([
        axios.get(`${API}/categories/${encodeURIComponent(brandName)}`),
        axios.get(`${API}/menu/${encodeURIComponent(brandName)}`)
      ]);
      setCategories(categoriesRes.data);
      setMenuItems(menuRes.data);
    } catch (error) {
      console.error("Error loading menu:", error);
      toast.error("Failed to load menu");
    }
  };

  const addCategory = async () => {
    if (!newCategory.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/categories`,
        {
          name: newCategory,
          brand_name: selectedBrand,
          display_order: categories.length
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Category added");
      setNewCategory("");
      loadMenuForBrand(selectedBrand);
    } catch (error) {
      console.error("Error adding category:", error);
      toast.error("Failed to add category");
    }
  };

  const deleteCategory = async (categoryName) => {
    if (!confirm(`Delete category "${categoryName}" and all its items?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(
        `${API}/categories/${encodeURIComponent(selectedBrand)}/${encodeURIComponent(categoryName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Category deleted");
      loadMenuForBrand(selectedBrand);
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    }
  };

  const openItemDialog = (item = null) => {
    if (item) {
      setItemForm(item);
      setEditingItem(item.name);
    } else {
      setItemForm({
        name: "",
        description: "",
        price: "",
        image_url: "",
        category: categories[0]?.name || "",
        available: true
      });
      setEditingItem(null);
    }
    setShowItemDialog(true);
  };

  const saveMenuItem = async () => {
    if (!itemForm.name || !itemForm.price || !itemForm.category) {
      toast.error("Name, price, and category are required");
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");
      const itemData = {
        ...itemForm,
        brand_name: selectedBrand,
        price: parseFloat(itemForm.price)
      };

      if (editingItem) {
        await axios.put(
          `${API}/menu/${encodeURIComponent(selectedBrand)}/${encodeURIComponent(editingItem)}`,
          itemData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/menu`, itemData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Item added");
      }

      setShowItemDialog(false);
      loadMenuForBrand(selectedBrand);
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("Failed to save item");
    }
  };

  const deleteMenuItem = async (itemName) => {
    if (!confirm(`Delete item "${itemName}"?`)) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(
        `${API}/menu/${encodeURIComponent(selectedBrand)}/${encodeURIComponent(itemName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Item deleted");
      loadMenuForBrand(selectedBrand);
    } catch { toast.error("Failed to delete item"); }
  };

  const toggleAvailability = async (itemName) => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.patch(
        `${API}/menu/${encodeURIComponent(selectedBrand)}/${encodeURIComponent(itemName)}/availability`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newState = res.data.available;
      setMenuItems(prev => prev.map(i => i.name === itemName ? { ...i, available: newState } : i));
      toast.success(newState ? "Item available" : "Item unavailable");
    } catch { toast.error("Failed to toggle availability"); }
  };

  const groupedItems = categories.reduce((acc, cat) => {
    acc[cat.name] = menuItems.filter(item => item.category === cat.name);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-6">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin-vow/kitchen")}
              className="rounded-full"
              data-testid="back-to-kitchen"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Menu Management</h1>
              <p className="text-sm text-muted-foreground">Manage categories and items</p>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Selector */}
      <div className="p-4">
        <Label className="mb-2 block">Select Brand</Label>
        <Select value={selectedBrand} onValueChange={setSelectedBrand}>
          <SelectTrigger className="rounded-xl" data-testid="brand-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {brands.map(brand => (
              <SelectItem key={brand.name} value={brand.name}>
                {brand.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="items" className="px-4">
        <TabsList className="grid w-full grid-cols-2 rounded-full">
          <TabsTrigger value="items" data-testid="items-tab">Menu Items</TabsTrigger>
          <TabsTrigger value="categories" data-testid="categories-tab">Categories</TabsTrigger>
        </TabsList>

        {/* Menu Items Tab */}
        <TabsContent value="items" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-foreground">Items</h2>
            <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => openItemDialog()}
                  className="rounded-full bg-primary hover:bg-primary/90"
                  data-testid="add-item-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingItem ? "Edit Item" : "Add Item"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      placeholder="Item name"
                      className="rounded-xl"
                      data-testid="item-name-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={itemForm.description}
                      onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                      placeholder="Item description"
                      className="rounded-xl"
                      data-testid="item-description-input"
                    />
                  </div>
                  <div>
                    <Label>Price (₹)</Label>
                    <Input
                      type="number"
                      value={itemForm.price}
                      onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                      placeholder="0.00"
                      className="rounded-xl"
                      data-testid="item-price-input"
                    />
                  </div>
                  <div>
                    <Label>Image URL</Label>
                    <Input
                      value={itemForm.image_url}
                      onChange={(e) => setItemForm({ ...itemForm, image_url: e.target.value })}
                      placeholder="https://..."
                      className="rounded-xl"
                      data-testid="item-image-input"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={itemForm.category} onValueChange={(val) => setItemForm({ ...itemForm, category: val })}>
                      <SelectTrigger className="rounded-xl" data-testid="item-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.name} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={saveMenuItem}
                    className="w-full rounded-full bg-primary hover:bg-primary/90"
                    data-testid="save-item-button"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Item
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No categories. Please add categories first.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {categories.map(category => (
                <div key={category.name}>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{category.name}</h3>
                  <div className="space-y-2">
                    {groupedItems[category.name]?.length > 0 ? (
                      groupedItems[category.name].map(item => (
                        <div
                          key={item.name}
                          className={`bg-card rounded-xl p-4 border border-border/50 flex items-center gap-4 ${item.available === false ? 'opacity-50' : ''}`}
                          data-testid={`menu-item-${item.name}`}
                        >
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-foreground">{item.name}</h4>
                            <p className="text-sm text-muted-foreground line-clamp-1">{item.description}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-accent font-semibold">₹{item.price}</span>
                              {item.diet_type && (
                                <span className="text-xs text-muted-foreground capitalize">· {item.diet_type}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Switch
                              checked={item.available !== false}
                              onCheckedChange={() => toggleAvailability(item.name)}
                              data-testid={`toggle-${item.name}`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openItemDialog(item)}
                              className="rounded-full"
                              data-testid={`edit-item-${item.name}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteMenuItem(item.name)}
                              className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                              data-testid={`delete-item-${item.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground p-4 text-center">No items in this category</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4 mt-4">
          <div>
            <Label className="mb-2 block">Add Category</Label>
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Category name"
                className="rounded-xl"
                data-testid="category-name-input"
                onKeyPress={(e) => e.key === 'Enter' && addCategory()}
              />
              <Button
                onClick={addCategory}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="add-category-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {categories.map(category => (
              <div
                key={category.name}
                className="bg-card rounded-xl p-4 border border-border/50 flex items-center justify-between"
                data-testid={`category-${category.name}`}
              >
                <div>
                  <h4 className="font-semibold text-foreground">{category.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {groupedItems[category.name]?.length || 0} items
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteCategory(category.name)}
                  className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid={`delete-category-${category.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MenuManagement;
